/**
 * WebSocket client wrapper for admin real-time updates
 */

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';
const WS_URL = API_URL.replace(/^http/, 'ws');

export type WebSocketMessage =
  | {
      type: 'new_message';
      conversation_id: string;
      message: {
        id: string;
        conversation_id: string;
        channel: string;
        direction: string;
        content: string;
        sent_by: string;
        sent_at: string;
        order_id: string | null;
        attachments: Record<string, any> | null;
        metadata_: Record<string, any> | null;
      };
    }
  | {
      type: 'order_update';
      order_id: string;
      status: string;
      order: Record<string, any> | null;
    }
  | {
      type: 'conversation_update';
      conversation_id: string;
      update_type: string;
      data: Record<string, any>;
    }
  | {
      type: 'dlq_update';
      dlq_id: string;
      action: string;
      data: Record<string, any>;
    }
  | {
      type: 'pong';
    };

export type WebSocketEventListener = (message: WebSocketMessage) => void;

export class WebSocketClient {
  private ws: WebSocket | null = null;
  private listeners: Set<WebSocketEventListener> = new Set();
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 3000; // 3 seconds
  private pingInterval: ReturnType<typeof setInterval> | null = null;

  constructor(
    private url: string = `${WS_URL}/api/v1/ws/admin`,
    private token?: string
  ) {}

  connect(): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      console.warn('WebSocket already connected');
      return;
    }

    try {
      // Connect without token in URL (security: avoid token in logs/history/referrer)
      this.ws = new WebSocket(this.url);

      this.ws.onopen = () => {
        console.log('WebSocket connected');
        // Send auth token as first message (not in URL)
        if (this.token) {
          this.ws!.send(JSON.stringify({ type: 'auth', token: this.token }));
        }
        this.reconnectAttempts = 0;
        this.startPing();
      };

      this.ws.onmessage = (event) => {
        try {
          const message: WebSocketMessage = JSON.parse(event.data);
          this.listeners.forEach((listener) => listener(message));
        } catch (error) {
          console.error('Failed to parse WebSocket message:', error);
        }
      };

      this.ws.onerror = (error) => {
        console.error('WebSocket error:', error);
      };

      this.ws.onclose = () => {
        console.log('WebSocket disconnected');
        this.stopPing();
        this.attemptReconnect();
      };
    } catch (error) {
      console.error('Failed to create WebSocket connection:', error);
      this.attemptReconnect();
    }
  }

  disconnect(): void {
    this.stopPing();
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
  }

  addListener(listener: WebSocketEventListener): void {
    this.listeners.add(listener);
  }

  removeListener(listener: WebSocketEventListener): void {
    this.listeners.delete(listener);
  }

  private startPing(): void {
    // Send ping every 30 seconds to keep connection alive
    this.pingInterval = setInterval(() => {
      if (this.ws?.readyState === WebSocket.OPEN) {
        this.ws.send('ping');
      }
    }, 30000);
  }

  private stopPing(): void {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
  }

  private attemptReconnect(): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('Max reconnect attempts reached');
      return;
    }

    this.reconnectAttempts++;
    console.log(
      `Attempting to reconnect (${this.reconnectAttempts}/${this.maxReconnectAttempts})...`
    );

    this.reconnectTimer = setTimeout(() => {
      this.connect();
    }, this.reconnectDelay);
  }
}
